import React, { useState } from 'react';
// import GoogleLogin from "react-google-login";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { EnvelopeIcon, LockClosedIcon as PasswordIcon } from '@heroicons/react/20/solid';

import Input from '../../components/form-control/Input';
import Button from '../../components/form-control/Button';
import { FORGOT_PASSWORD } from '../../resources/links';
import { toast } from 'react-toastify';
import { ILoginDTO } from '../../resources/interfaces';
import useRoute from '../../hooks/routesHook';
import MessagePrompt from '../../components/elements/MessagePrompt';
import { CheckBoxInput } from '../../components/form-control/CheckBoxInput';
import useUserAccount from '../../hooks/stateHooks/userAccountHook';
import Link from '../../components/Link';
import clsx from 'clsx';
import { loginWithPassword } from '../../api/requests';
import { User } from '../../components/models/User';
import { AxiosError } from 'axios';
import { companyDataToCompanyDTO } from '../../resources/converters';

function SignIn() {
  const navigate = useNavigate();

  const { getAccount, setProfile, signOut, updateCompanyOnboarding } = useUserAccount();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { getDashboardBase } = useRoute();
  const [searchParams] = useSearchParams();

  const sessionExpired = getAccount().sessionExpired;

  React.useEffect(() => {
    if (!!searchParams.get("activateAccount")) {
      signOut();
    };

    if (getAccount().isAuthenticated) {
      navigate(`${getDashboardBase()}/dashboard`, { replace: true });
    }
  }, []);

  const { setAccount } = useUserAccount();

  const handleLogin = (values: any) => {
    setSubmitting(true);
    const payload: ILoginDTO = {
      username: values.email.trim(),
      password: values.password
    };

    loginWithPassword(payload)
      .then((response: any) => {
        setAccount({
          isNew: response?.data?.isNew ?? false,
          accessRight: null,
          accountSession: '',
          userId: '',
          token: response?.data?.token,
          sessionExpired: false
        });

        const user = new User(response?.data?.user);

        setProfile({
          id: user?.id,
          first_name: user?.firstName,
          last_name: user?.lastName,
          email: user?.email,
          gender: user?.gender,
          avatar_url: user.profileImage,
          role: user.role,
          is_active: user.isActive,
          company: user.company,
        });

        if (response.data.user.company) {
          updateCompanyOnboarding({
            is_onboarded: !!response.data.user.company.activated_at,
            current_step: 0,
            data: companyDataToCompanyDTO(response.data.user.company)
          })
        }
        navigate("/admin/dashboard", { replace: true });
      })
      .catch((error: any | AxiosError) => {
        toast.error(`${error?.detail}.`, {
          autoClose: 5000
        });
      }).finally(() => setSubmitting(false));
    // };
  };

  return (
    <div
      className={clsx(
        '  bg-white mx-1 rounded-md py-16 md:mx-14 font-poppins w-full md:w-1/2 lg:w-1/3'
      )}>
      <h3 className='text-[30px] w-full text-center font-semibold'>Sign in</h3>
      <div className="text-center font-normal font-poppins">
        {sessionExpired ? (
          <MessagePrompt
            type="error"
            message="Account session expired due to inactivity. Kindly log in again"
            showInfoIcon={false}
          />
        ) : (
          <p className="text-[#9CA3AF]">Welcome, please enter your credentials to login.</p>
        )}
      </div>

      <Formik
        initialValues={{
          email: '',
          password: '',
          rememberMe: false
        }}
        validateOnChange
        validateOnBlur
        validationSchema={Yup.object({
          email: Yup.string().trim().email('Invalid email').required('Email is required'),
          password: Yup.string().trim().required('Password is required'),
          rememberMe: Yup.boolean()
        })}
        onSubmit={async (values) => {
          handleLogin(values);
        }}>
        {({
          values,
          errors,
          isSubmitting,
          handleSubmit,
          handleChange,
          setFieldValue,
          handleBlur,
          touched
        }) => (
          <form className="px-3 md:px-24 lg:px-12 mt-10" onSubmit={handleSubmit}>
            <div className="my-5">
              <Input
                label="Email"
                name="email"
                required
                wrapperClasses="my-5"
                value={values.email.toLowerCase()}
                prepend={<EnvelopeIcon className="w-6 h-6 " />}
                onChange={handleChange}
                error={touched.email ? errors.email : undefined}
                helperText={touched.email ? errors.email : undefined}
                placeholder="Enter your email address"
              />

              <Input
                name="password"
                label="Password"
                value={values.password}
                type="password"
                wrapperClasses="my-5"
                showPasswordIcon
                required
                prepend={<PasswordIcon className="w-6 h-6" />}
                onChange={handleChange}
                error={touched.password ? errors.password : undefined}
                helperText={touched?.password ? errors.password : undefined}
                placeholder="Enter your password"
              />

              <div className="flex items-center justify-between ">
                <CheckBoxInput
                  name={'rememberMe'}
                  onChange={(val: boolean) => {
                    setFieldValue('rememberMe', val);
                  }}
                  label={'Remember Me'}
                  checked={values.rememberMe}
                />

                <div className="text-sm text-[#004E60]">
                  <Link to={`/auth/${FORGOT_PASSWORD}`} label={'Forgot password?'} />
                </div>
              </div>

              <Button
                type="submit"
                label="Sign In"
                className="mt-5 flex justify-center rounded px-6 w-full bg-secondary-main text-white shadow-md"
                loading={submitting}
                disabled={submitting}
              />
            </div>
          </form>
        )}
      </Formik>
      {/* <p>New to HelloSusu? <Link to={`${getDashboardBase()}/${SiGN}`} label={'Sign up'} /></p> */}
    </div>
  );
}

export default SignIn;
